<template>
    <div class="d-flex flex-column case-session-container">
        <div class="flex-grow-0">
            <nav
                class="d-flex align-items-md-start flex-wrap flex-md-nowrap justify-content-end justify-content-lg-start mt-2 mx-2"
            >
                <div class="flex-grow-1 session-navbar">
                    <ul v-if="sessionCase" class="d-flex session-navbar mr-auto pl-md-1">
                        <!-- Back to Page -->
                        <li v-if="isPageViewerSession">
                            <router-link
                                class="nav-link text-secondary nowrap px-1 px-sm-3 medium"
                                :to="pageViewerRoute"
                                aria-label="Return to library"
                            >
                                <span
                                    aria-hidden="true"
                                    class="icon icon-sm icon-glyphicons-basic-chevron-left mr-1 align-middle"
                                ></span>
                                <span class="align-middle d-sm-none">Back</span>
                                <span class="align-middle d-none d-sm-inline-block page-back-label"
                                    >Back to: {{ currentSession.publish_page.title }}</span
                                >
                            </router-link>
                        </li>
                        <!-- Back to Community Detail View -->
                        <li v-else-if="communityViewActive && !iframeIsActive">
                            <router-link
                                class="nav-link pl-md-0 text-secondary mr-sm-2 global-theme"
                                :to="communityCaseDetailRoute"
                                aria-label="Return to the community library"
                            >
                                <span
                                    aria-hidden="true"
                                    class="icon icon-sm icon-glyphicons-basic-chevron-left mr-1 align-middle"
                                ></span>
                                <span class="align-middle d-none d-md-inline">Back to Browse</span>
                            </router-link>
                        </li>
                        <!-- Logo Display -->
                        <li
                            v-else-if="showLogo"
                            class="nav-item d-none d-sm-block pl-0 pl-md-2"
                            :class="{ 'mt-0': customLogoIsDisplayed }"
                        >
                            <div
                                v-if="customLogoIsDisplayed"
                                class="flex-grow-0 logo-sm d-flex align-items-center rounded-circle custom-logo-circle"
                            >
                                <img :src="brandLogoPath" :alt="owningOrganizationName + ' logo'" />
                            </div>
                            <a
                                v-else
                                class="nav-link px-1 pl-md-0"
                                href="https://affinitylearning.ca/"
                                target="_blank"
                                aria-label="Visit the Affinity Learning website"
                            >
                                <img :src="defaultLogoPath" alt="Visit Affinity Learning website" height="20" />
                            </a>
                        </li>
                        <!-- Logo Display - No Links -->
                        <li
                            v-else
                            class="nav-item d-none d-sm-block pl-0 pl-md-2"
                            :class="{ 'mt-0': customLogoIsDisplayed }"
                        >
                            <div
                                v-if="customLogoIsDisplayed"
                                class="flex-grow-0 logo-sm d-flex align-items-center rounded-circle custom-logo-circle"
                            >
                                <img :src="brandLogoPath" :alt="owningOrganizationName + ' logo'" />
                            </div>
                            <img v-else :src="defaultLogoPath" alt="Affinity Learning logo" height="20" />
                        </li>
                    </ul>
                </div>
                <div class="flex-grow-0 session-navbar">
                    <li v-if="shareLinkNavigationEnabled && currentNode" class="nav-item d-none d-sm-block mr-3">
                        <span class="nav-link d-flex">
                            <div v-show="videoIsPlaying && !loading">
                                <button class="btn btn-gray badge badge-gray mr-3" @click="skipVideos()">
                                    <span class="align-middle" style="margin-right: 2px;">Skip Video</span>
                                    <span class="icon icon-glyphicons-basic-skip-to-next align-middle"></span>
                                </button>
                            </div>
                            <session-node-dropdown
                                :current-node="currentNode"
                                v-on:select="viewNode($event)"
                            ></session-node-dropdown>
                        </span>
                    </li>
                    <li v-else-if="commentsEnabled && currentNode" class="nav-item d-none d-sm-block mr-3">
                        <span class="nav-link">
                            <span class="badge badge-gray">{{ currentNode.number }} - {{ currentNode.title }}</span>
                        </span>
                    </li>
                    <li v-else-if="learnerViewActive || communityViewActive" class="nav-item mr-3 d-none d-md-block">
                        <span class="nav-link mb-0">
                            <info-popover :content="'Viewing: ' + caseDisplayTitle" placement="bottom">
                                <span class="badge badge-gray align-middle display-title cursor-default">{{ caseDisplayTitle }}</span>
                            </info-popover>
                        </span>
                    </li>
                </div>
                <div class="flex-grow-0 nav-scroll-x">
                    <session-toolbar
                        v-if="sessionCase && sessionInitialized"
                        :case-obj="sessionCase"
                        :show-comments-btn="commentsEnabled"
                        :show-author-tools="false"
                        v-on:timer-expired="timerExpired($event)"
                    ></session-toolbar>
                </div>
            </nav>
        </div>
        <case-session
            :session-id="sessionID"
            v-on:nodeChange="setCurrentNode($event)"
            v-on:return-to-page="returnToPage()"
            :sessionInitialized.sync="sessionInitialized"
            ref="caseSession"
        ></case-session>
    </div>
</template>

<script>
import http from "@/axios.js"
import CaseSession from "@/components/CaseSession"
import SessionNodeDropdown from "@/components/SessionNodeDropdown"
import SessionToolbar from "@/components/SessionToolbar"
import InfoPopover from "@/components/InfoPopover"

export default {
    name: "LearnerCaseSession",
    components: {
        CaseSession,
        SessionToolbar,
        SessionNodeDropdown,
        InfoPopover,
    },
    data() {
        return {
            modalLoading: false,
            defaultLogoPath: "/static/general/img/affinity-blue.svg",
            sessionInitialized: false,
            nodeList: [],
            iframeIsActive: window !== window.top,
        }
    },
    computed: {
        loading() {
            return this.$store.getters.loading
        },
        sessionID() {
            return this.$route.params.sessionID
        },
        caseTitle() {
            return this.$store.getters.sessionCaseString
        },
        caseDisplayTitle() {
            if (this.communityViewActive && this.sessionCase) {
                return this.sessionCase.community_title
            }
            return this.caseTitle
        },
        showLogo() {
            // Hide the logo if the session is iframed and it's not a share link session,
            // in order to hide the logo when iframed into an LMS (where showing the logo 
            // is redundant).
            if (!this.isShareLinkSession && this.iframeIsActive) {
                return false
            }
            return this.$store.getters.userIsAnonymous && !this.communityViewActive
        },
        communityViewActive() {
            return this.$route.path.startsWith("/community")
        },
        currentNode() {
            return this.$store.getters.sessionNode
        },
        sessionCase() {
            return this.$store.getters.sessionCase
        },
        currentSession() {
            return this.$store.getters.session
        },
        shareLink() {
            return this.currentSession.share_link
        },
        isShareLinkSession() {
            return Boolean(this.shareLink)
        },
        isPageViewerSession() {
            return Boolean(this.$route.params.pageID)
        },
        pageViewerRoute() {
            if (this.$route.params.shareLinkID) {
                return {
                    name: "PageViewerShare",
                    params: {
                        id: this.$route.params.pageID,
                        shareLinkID: this.$route.params.shareLinkID,
                    },
                }
            }
            return {
                name: "PageViewer",
                params: { id: this.$route.params.pageID },
            }
        },
        hideNavLinks() {
            return this.currentSession.hide_nav_links
        },
        learnerViewActive() {
            return this.$route.name == "LearnerCaseSession"
        },
        commentsEnabled() {
            if (!this.shareLink) {
                return false
            }
            return this.shareLink.allow_comments
        },
        communityCaseDetailRoute() {
            if (!this.sessionCase) {
                return "/"
            }
            return { name: "CommunityCaseDetail", params: { id: this.sessionCase.id } }
        },
        owningOrganization() {
            if (!this.sessionCase) return null
            return this.sessionCase.organization || null
        },
        owningOrganizationName() {
            if (!this.owningOrganization) {
                return "Affinity Learning"
            }
            return this.owningOrganization.name
        },
        brandLogoPath() {
            return this.$store.getters.customLogoUrl
        },
        customLogoIsDisplayed() {
            return this.brandLogoPath !== null
        },
        shareLinkNavigationEnabled() {
            return this.shareLink && this.shareLink.enable_node_navigation
        },
        videoIsPlaying() {
            return this.$store.getters.videoIsPlaying
        },
    },
    methods: {
        setCurrentNode(node) {
            this.$store.commit("setSessionNode", node)
        },
        timerExpired(timerId) {
            this.$refs.caseSession.timerExpired(timerId)
            this.$refs.caseSession.updateSessionScore()
        },
        returnToPage() {
            this.$router.push(this.pageViewerRoute)
        },
        // View a different node using the same session.
        viewNode(node) {
            if (this.currentNode.id === node.id) {
                return
            }
            this.$store.dispatch("addLoad")
            let vm = this
            http.put(`/api/sessions/${this.sessionID}/switch-nodes/`, {
                nodeId: node.id,
            })
                .then(() => {
                    vm.$refs.caseSession.refreshSession()
                })
                .finally(() => {
                    vm.$store.dispatch("removeLoad")
                })
        },
        skipVideos() {
            this.$refs.caseSession.skipVideos()
        },
    },
    created() {
        // Make sure preview edit mode is disabled.
        this.$store.commit("setPreviewEditMode", false)
    },
    destroyed() {
        this.$store.commit("setSessionNode", null)
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";

li {
    list-style-type: none;
}

ul {
    padding-left: 0px;
    padding-right: 0px;
    margin-bottom: 0px;
}

.display-title {
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 280px;
    overflow: hidden;
    text-overflow: ellipsis;
}

.nav-item {
    margin-top: 3px;
}

.custom-logo {
    margin-top: 8px;
}

.page-back-label {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    max-width: 340px;
}
</style>